import { BilberryPromoCodeStatus, CartItem } from '@repo/types';
import { createIntentMultiRequest } from '@repo/widget-utils/booking/createIntent';
import {
    removeInvalidCartItems,
    shouldUsePaymentPlansForCart,
    filterBookableItems,
} from '@repo/widget-utils/cart/cartUtils';
import { isEqual } from 'lodash-es';
import { useMemo } from 'react';
import { useDebounce } from 'src/hooks/common/useDebounce';
import { IgnoredValueCards } from 'src/state/voucher/voucherAtom';
import { useMemberContext } from 'src/widgets/timeslots/timeslots/MemberContext';

export default function useIntentMultiRequest(
    isCompanyContact: boolean,
    cartItems: CartItem[],
    appliedPromoCode: BilberryPromoCodeStatus | null,
    ignoredValueCards: IgnoredValueCards,
) {
    const { currentSite } = useMemberContext();

    const validCartItems = useMemo(() => {
        const bookableCartItems = filterBookableItems(cartItems);
        return removeInvalidCartItems(bookableCartItems);
    }, [cartItems]);

    const isUsingPaymentPlan = useMemo(() => {
        return shouldUsePaymentPlansForCart(validCartItems, currentSite, isCompanyContact);
    }, [validCartItems, currentSite, isCompanyContact]);

    const request = useMemo(() => {
        return isUsingPaymentPlan
            ? createIntentMultiRequest(
                  validCartItems,
                  ignoredValueCards.valueCardIdsToIgnore,
                  ignoredValueCards.valueCardProductIdsToIgnore,
                  ignoredValueCards.valueCardProductTypeIdsToIgnore,
                  appliedPromoCode?.coupon_code,
              )
            : null;
    }, [isUsingPaymentPlan, validCartItems, appliedPromoCode, ignoredValueCards]);

    const debouncedRequest = useDebounce(request, 500);

    return {
        isUsingPaymentPlan,
        request: debouncedRequest,
        isRequestChanging: !isEqual(request, debouncedRequest),
    };
}
